import { checkStatus } from '../../utilities/fetch';
import { createQueryString } from '../../utilities/utilities';
import { bookmarkedSearchesPath, alertsPath } from '../../utilities/api_relative_paths';

/**
 * @param {string} search_type - search_type of bookmarked searches
 * @param {number} pageSize - page size to be applied
 * @returns {Promise} - Promise that can be extended
 */
const getBookmarkedSearches = (search_type, pageSize) =>
  fetch(bookmarkedSearchesPath + createQueryString({ search_type, page: { size: pageSize } }))
    .then(checkStatus)
    .then((response) => response.json())
    .catch((error) => Promise.reject(error));

/**
 * @param {string} search_type - search_type of bookmarked searches
 * @param {object} params - Object containing different filter values
 * @param {string} title - Name of the bookmarked search to be created
 *
 * @returns {Promise} - Promise that can be extended
 */
const createBookmarkedSearch = (search_type, params, title) => {
  const requestOptions = {
    method: 'POST',
    body: JSON.stringify({title, params, search_type}),
    headers: { 'Content-Type': 'application/json' },
  };

  return fetch(bookmarkedSearchesPath, requestOptions)
    .then(checkStatus)
    .then((response) => response.json())
    .catch((error) => Promise.reject(error));
};

/**
 * @param {number} [id] - ID of the bookmarked search to delete
 * @returns {Promise} - Promise that can be consumed
 */
const deleteBookmarkSearch = (id) => {
  if (!id) return Promise.reject('Invalid ID parameter');

  return fetch(`${bookmarkedSearchesPath}/${id}`, { method: 'DELETE' })
    .then(checkStatus)
    .then((response) => response.json())
    .catch((error) => Promise.reject(error));
};

/**
 * Given a bookmarked search ID, create an alert for it.
 *
 * @param {number} [interval] - Interval as integer. Can be 1 (daily) or 7 (weekly)
 * @param {number} [alertable_id] - ID of saved search
 * @returns {Promise} - Promise that can be consumed
 */
const createAlert = (interval, alertable_id) => {
  if (!interval || !alertable_id) return Promise.reject('Invalid parameters');

  const requestOptions = {
    method: 'POST',
    body: JSON.stringify({ interval, alertable_id, alertable_type: 'BookmarkedSearch' }),
    headers: { 'Content-Type': 'application/json' },
  };

  return fetch(alertsPath, requestOptions)
    .then(checkStatus)
    .then((response) => response.json())
    .catch((error) => Promise.reject(error));
};

/**
 * Edits alert interval
 *
 * @param {number} [id] - ID of saved alert
 * @param {number} [interval] - Interval as integer. Can be 1 (daily) or 7 (weekly)
 * @returns {Promise} - Promise that can be consumed
 */
const editAlert = (id, interval) => {
  if (!interval || !id) return Promise.reject('Invalid parameters');

  const requestOptions = {
    method: 'PATCH',
    body: JSON.stringify({ interval, }),
    headers: { 'Content-Type': 'application/json' },
  };

  return fetch(`${alertsPath}/${id}`, requestOptions)
    .then(checkStatus)
    .then((response) => response.json())
    .catch((error) => Promise.reject(error));
};

/**
 * @param {number} id - ID of the alert to delete
 * @returns {Promise} - Promise that can be consumed
 */
const deleteAlert = (id) => {
  if (!id) return Promise.reject('Invalid ID parameter');

  return fetch(`${alertsPath}/${id}`, { method: 'DELETE' })
    .then(checkStatus)
    .then((response) => response.json())
    .catch((error) => Promise.reject(error));
};

export {
  getBookmarkedSearches,
  createBookmarkedSearch,
  deleteBookmarkSearch,
  createAlert,
  editAlert,
  deleteAlert,
};
